import {
  Box,
  Button,
  Grid,
  Popover,
  Stack,
  SxProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useState, useMemo, useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import HistoryIcon from '@mui/icons-material/History'
import ButtonField from '@/components/form-control/ButtonField'
import Select from '@/components/form-control/Select'
import DateTimeControl from '@/components/form-control/DateTimeControl'
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash'
import { DATA_PAGINATION } from '@/types'
import { ONE_DAY } from '@/constant/date'
import { TimeGrouping } from '@/store/types/DataComparisonType'
// import classes from './styles.module.scss'

// Time window modes
export enum TimeWindowMode {
  REALTIME = 'realtime',
  HISTORY = 'history',
}
interface ITimeOption {
  label: string
  value: number | string
}

// Quick time intervals (in milliseconds)
export const TIME_LAST = [
  { label: 'Last 24h', value: 1 * ONE_DAY },
  { label: 'Last 3 days', value: 3 * ONE_DAY },
  { label: 'Last 5 days', value: 5 * ONE_DAY },
  { label: 'Last 7 days', value: 7 * ONE_DAY },
  { label: 'Last 30 days', value: 30 * ONE_DAY },
]

export const TIME_GROUPING_INTERVAL = [
  { label: '1 minutes', value: TimeGrouping.ONE_MIN },
  { label: '5 minutes', value: TimeGrouping.FIVE_MINS },
  { label: '15 minutes', value: TimeGrouping.FIFTEEN_MINS },
  // { label: '30 minutes', value: 30 * ONE_MINUTE },
  { label: '1 hour', value: TimeGrouping.ONE_HOUR },
  { label: '6 hours', value: TimeGrouping.SIX_HOURS },
  { label: '1 day', value: TimeGrouping.ONE_DAY },
  { label: '2 days', value: TimeGrouping.TWO_DAYS },
]

export interface TimeWindow {
  mode: TimeWindowMode
  interval?: number // For realtime mode
  startDate?: Date // For history mode
  endDate?: Date // For history mode
  aggregation?: string // 'AVG', 'MIN', 'MAX', 'SUM', 'COUNT'
}

interface TimeWindowControlProps {
  name: string
  form: UseFormReturn<any, any>
  sxProps?: {
    sxButton?: SxProps
  }
  onApply?: () => void
}

const TimeWindowControl = ({ name, form, sxProps, onApply }: TimeWindowControlProps) => {
  const timeWindowConfig = form.watch(name) || {}
  const mode = timeWindowConfig?.timeType
  const timeLast = timeWindowConfig?.timeLast
  const startDate = timeWindowConfig?.startDate
  const endDate = timeWindowConfig?.endDate
  const timeGroupingInterval = timeWindowConfig?.timeGroupingInterval

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [oldConfig, setOldConfig] = useState<any>(null)

  const setMode = (mode: TimeWindowMode) => {
    form.setValue(`${name}.timeType`, mode)
  }

  const open = Boolean(anchorEl)

  useEffect(() => {
    if (form && name) {
      setDataDefault()
    }
  }, [form, name, timeWindowConfig])

  // Display text
  const displayText = useMemo(() => {
    const groupingLabel = timeGroupingInterval?.label
    if (mode === TimeWindowMode.REALTIME) {
      const base = timeLast?.label || 'Real-time'
      return groupingLabel ? `${base} (${groupingLabel})` : base
    } else {
      if (startDate && endDate) {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const base = `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`
        return groupingLabel ? `${base} (${groupingLabel})` : base
      }
      return 'Select time range'
    }
  }, [mode, timeLast, startDate, endDate, timeGroupingInterval])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOldConfig(cloneDeep(form.getValues(name)))
    setAnchorEl(event.currentTarget)
  }

  const setDataDefault = () => {
    if (!timeWindowConfig?.timeType) {
      setMode(TimeWindowMode.REALTIME)
    }
    if (!timeWindowConfig?.timeLast) {
      form.setValue(`${name}.timeLast`, TIME_LAST[1])
    }
    if (!timeWindowConfig?.timeGroupingInterval) {
      form.setValue(`${name}.timeGroupingInterval`, TIME_GROUPING_INTERVAL[2])
    }
    if (!timeWindowConfig?.startDate) {
      form.setValue(`${name}.startDate`, new Date().toISOString())
      form.setValue(`${name}.endDate`, new Date().toISOString())
    }
  }

  const handleClose = () => {
    form.setValue(name, oldConfig)
    setAnchorEl(null)
  }

  const handleApply = () => {
    form.setValue(name, cloneDeep(form.getValues(name)))
    setAnchorEl(null)
    onApply?.()
  }

  const handleModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: TimeWindowMode | null,
  ) => {
    if (newMode !== null) {
      setMode(newMode)
    }
  }

  const getOptions = (dataOption: ITimeOption[]) => () => {
    let data: DATA_PAGINATION | undefined

    data = {
      data: dataOption,
      page: 1,
      size: dataOption.length,
      totalCount: dataOption.length,
      totalPage: 1,
    }
    return Promise.resolve(data)
  }

  return (
    <Stack
      spacing="12px"
      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
    >
      <ButtonField
        startIcon={
          mode === TimeWindowMode.REALTIME ? (
            <AccessTimeIcon sx={{ color: 'var(--color-button-icon)' }} />
          ) : (
            <HistoryIcon sx={{ color: 'var(--color-button-icon)' }} />
          )
        }
        style={{
          height: '32px',
          minWidth: '89px',
          borderRadius: '5px',
          margin: '0 0 0 8px',
        }}
        name={displayText}
        onClick={handleClick}
        sx={sxProps?.sxButton}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            backgroundColor: 'var(--backgroundColor-box)',
            minWidth: 450,
          },
        }}
      >
        <Stack spacing={2}>
          {/* Mode Toggle */}
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            fullWidth
            size="small"
            sx={{
              '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                borderRadius: '5px !important',
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '115%',
                '&:not(.Mui-selected)': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#e6eded' : '#e6eded',
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#004744' : '#004744',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark' ? '#d5e5e5' : '#d5e5e5',
                  },
                },
                '&.Mui-selected': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '#004744 !important'
                      : '#004744 !important',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: '#003a37 !important',
                  },
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              },

              // Gap between buttons
              gap: '8px',
            }}
          >
            <ToggleButton value={TimeWindowMode.REALTIME}>
              <AccessTimeIcon sx={{ mr: 1 }} />
              Real-time
            </ToggleButton>
            <ToggleButton value={TimeWindowMode.HISTORY}>
              <HistoryIcon sx={{ mr: 1 }} />
              History
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Real-time Mode: Quick Intervals */}
          {mode === TimeWindowMode.REALTIME && (
            <Box gap={'16px'}>
              <Select
                minWidth={'100%'}
                label={' Select time'}
                name={`${name}.timeLast`}
                bindLabel="label"
                bindKey="value"
                placeholder=" Select time"
                form={form}
                getOptions={getOptions(TIME_LAST)}
              />
              <Select
                minWidth={'100%'}
                label={'Grouping interval'}
                name={`${name}.timeGroupingInterval`}
                bindLabel="label"
                bindKey="value"
                placeholder="Grouping interval"
                form={form}
                getOptions={getOptions(TIME_GROUPING_INTERVAL)}
              />
            </Box>
          )}

          {/* History Mode: Custom Date Range */}
          {mode === TimeWindowMode.HISTORY && (
            <Stack>
              <Typography variant="body2" sx={{ color: 'text.main' }}>
                Select time range:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DateTimeControl
                    format="dd/MM/yyyy"
                    label="From"
                    //onChange={(value:any) => props.setEndDate(value)}
                    views={['year', 'month', 'day']}
                    form={form}
                    // startIcon
                    name={`${name}.startDate`}
                    labelStart="End:"
                    maxDate={dayjs(form.watch(`${name}.endDate`)).toDate()}
                    className="dateTimeControl"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '100%' },
                      marginTop: '8px',
                      padding: '2px 16px',
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DateTimeControl
                    format="dd/MM/yyyy"
                    label="To"
                    views={['year', 'month', 'day']}
                    form={form}
                    name={`${name}.endDate`}
                    labelStart="End:"
                    maxDate={dayjs().add(1, 'day').toDate()}
                    minDate={dayjs(form.watch(`${name}.startDate`)).toDate()}
                    className="dateTimeControl"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '100%' },
                      marginTop: '8px',
                      padding: '2px 16px',
                    }}
                  />
                </Grid>
              </Grid>

              <Select
                minWidth={'100%'}
                label={' Grouping interval'}
                name={`${name}.timeGroupingInterval`}
                bindLabel="label"
                bindKey="value"
                placeholder="Grouping interval"
                form={form}
                getOptions={getOptions(TIME_GROUPING_INTERVAL)}
              />
            </Stack>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleClose} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              size="small"
              disabled={
                mode === TimeWindowMode.HISTORY && (!startDate || !endDate)
              }
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  )
}

export default TimeWindowControl
